import {Barcode, NfcCard} from "@variocube/app-api";
import {
    BackButton,
    classNames,
    layout,
    PushButton,
    typography,
    useBarcodeListener,
    useCardListener,
    useStorageItemValue,
    View,
    ViewContent,
    ViewHeader,
    ViewHeaderItem,
    ViewTitle
} from "@variocube/cube-ui";
import * as React from "react";
import {useState} from "react";
import {stylesheet} from "typestyle";
import {useLocalization} from "../i18n";
import {Recipient} from "./types";
import {logger} from "./logger";

interface AuthorizeUserDialogProps {
    authorizationProviderHint?: string;
    onBack: () => void;
    onSubmit: (recipient: Recipient | undefined) => void;
}

const log = logger.sub("AuthorizeUserDialog");

export function AuthorizeUserDialog({authorizationProviderHint, onBack, onSubmit}: AuthorizeUserDialogProps) {
    const {t} = useLocalization();
    const [error, setError] = useState('');
    const recipients = useStorageItemValue<Recipient[]>("recipients") || [];
    const [recipient, setRecipient] = useState<Recipient>();

    function handleSubmit() {
        onSubmit(recipient);
    }

    const handleBarcode = (code: Barcode) => {
		log.info("Received barcode", code);
        try {
            const barOrQRcode = atob(code.code);
            tryFindRecipient(barOrQRcode);
        } catch (error) {
            log.error("Error handling barcode", code);
        }
    }

    const handleCard = (card?: NfcCard) => {
		log.info("Received card", card);
        try {
            if (card) {
                const code = atob(card.uid);
                tryFindRecipient(code);
            }
        } catch (error) {
            log.error("Error handling card", card);
        }
    };

    useCardListener(handleCard);
	useBarcodeListener(handleBarcode);

    function tryFindRecipient(code: string) {
        let scannedCode = code.trim();
        if (recipients) {
            const found = recipients.find(r => r.pickupKey === scannedCode || r.learnedPickupKey === scannedCode);
            setRecipient(found);
			setError(!found ? t('NoRecipientFound') : '');
        } else {
            log.error("Cannot find recipient by code", scannedCode);
        }
    }

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem>
                    <BackButton onClick={onBack} label={t("Back")}/>
                </ViewHeaderItem>
                <ViewHeaderItem fill>
                    <ViewTitle>{t("Return")}</ViewTitle>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={classNames(styles.content, layout.vertical, layout.center)}>

                {!recipient && (
                    <div className={classNames(typography.title, layout.paddingLarge)}>
                        {authorizationProviderHint ? authorizationProviderHint : t('AuthorizationProviderHint')}
                    </div>
                )}

                {recipient && (
                    <div className={classNames(layout.paddingLarge, layout.center)}>
                        <div className={typography.alignCenter}>{t("AuthenticatedAs")}</div>
                        <div className={typography.title}>{recipient.recipientName}</div>
                    </div>
                )}
                <div className={classNames(styles.Error, typography.colorError)}>{error}</div>
                <div>
                    {recipient && (
                        <PushButton primary onClick={handleSubmit} className={classNames(styles.action)}>
                            {t("Next")}
                        </PushButton>
                    )}
                </div>
            </ViewContent>
        </View>
    );
}

const styles = stylesheet({
    label: {
        fontSize: "1.5rem",
    },
    textInput: {
        fontWeight: "bold",
        width: "90%",
        height: "4rem",
        fontSize: "2em",
        margin: "0.3em"
    },
    keyboard: {
        backgroundColor: "transparent",
        fontSize: "1.25rem",
    },
    content: {
        display: "flex",
        alignItems: "center",
        justifyContent: "stretch",
    },
    keyboardContainer: {
        padding: "0.25rem"
    },
    Checkbox: {
        height: "1em",
        width: "1em",
        display: "inline",
        margin: "1em"
    },
    Error: {
        textAlign: "center",
        fontSize: "2em",
        fontWeight: "bold",
    },
    action: {
        cursor: "pointer"
    }
})


